import { createResourceLocatorString } from './routes';
import { createSlug } from './urlHelpers';

export const getStoreBlogId = blog => blog?._id || blog?.id || '';

export const getStoreBlogSlug = blog => {
  const slug = blog?.slug;
  return typeof slug === 'string' && slug.trim() ? slug.trim() : '';
};

const randomSlugSuffix = (length = 12) => {
  const chars = 'abcdef0123456789';

  return Array.from({ length }, () => {
    const index = Math.floor(Math.random() * chars.length);
    return chars[index];
  }).join('');
};

export const buildStoreBlogSlug = blog => {
  const existingSlug = getStoreBlogSlug(blog);
  if (existingSlug) {
    return existingSlug;
  }

  const title =
    blog?.title ||
    blog?.translations?.en?.title ||
    blog?.translations?.es?.title ||
    blog?.translations?.fr?.title ||
    blog?.translations?.zh?.title ||
    '';

  const baseSlug = createSlug(title);
  return `${baseSlug}-${randomSlugSuffix()}`;
};

export const getStoreBlogLinkTarget = (blog, userId) => {
  const normalizedUserId = userId ? String(userId) : '';
  const blogId = getStoreBlogId(blog);
  const slug = getStoreBlogSlug(blog);

  if (normalizedUserId && slug) {
    return {
      routeName: 'StoreBlogContentPage',
      params: { userId: normalizedUserId, slug },
    };
  }

  if (blogId && normalizedUserId) {
    return {
      routeName: 'StoreBlogContentPageLegacy',
      params: { blogId: String(blogId), userId: normalizedUserId },
    };
  }

  return null;
};

export const createStoreBlogPath = (
  blog,
  userId,
  routes,
  extraPathParams = {},
  searchParams = {}
) => {
  const target = getStoreBlogLinkTarget(blog, userId);
  if (!target) {
    return null;
  }

  return createResourceLocatorString(
    target.routeName,
    routes,
    { ...target.params, ...extraPathParams },
    searchParams
  );
};

const dedupeBlogsByKey = blogs => {
  const seen = new Map();
  (blogs || []).forEach(blog => {
    const id = blog?._id || blog?.id;
    if (!id) return;
    const listingId = blog?.listingId || '';
    const key = `${id}:${listingId}`;
    if (!seen.has(key)) {
      seen.set(key, blog);
    }
  });
  return Array.from(seen.values());
};

/**
 * Whether a customer would see any blog content on the store profile Blog tab
 * (published store blogs or listing blogs shown in store). Mirrors StorefrontTabContent blog filtering.
 */
export const hasCustomerVisibleStoreBlogs = (storeBlogs = []) => {
  if (!Array.isArray(storeBlogs) || storeBlogs.length === 0) {
    return false;
  }
  const publishedBlogs = dedupeBlogsByKey(storeBlogs).filter(
    b => !b.status || b.status === 'published'
  );
  const listingBlogs = publishedBlogs.filter(blog => !!blog?.showInStore);
  const publishedStoreBlogs = publishedBlogs.filter(blog => !blog?.showInStore);
  return publishedStoreBlogs.length > 0 || listingBlogs.length > 0;
};
